import { FC } from 'react';

import Link from 'next/link';
import router from 'next/router';

import { Collapse } from '@mui/material';

import { IoMenuSharp } from 'react-icons/io5';
import { MdClose } from 'react-icons/md';
import { useQuery } from 'react-query';
import { useRecoilValue } from 'recoil';

import { useDashboardData } from '@hooks_iqfu/dashboard/useDashboardData';

import { companyLoginUserDataAtom, companyLoginUserDataSelector } from '@recoil_iqfu/atoms/auth/companyLoginUserData';

import { TargetMenu, useMenuToggle } from '@hooks_common/useMenuToggle';

import styles from './SideMenu.module.scss';

type Props = {
  toggleSpMenu: () => void;
  spMenuOpen: boolean;
};

const CompanySideMenu: FC<Props> = ({ toggleSpMenu, spMenuOpen }) => {
  const { getCompanyDashboardData } = useDashboardData();
  const { data: dashboardData } = useQuery(['getCompanyDashboardData'], () => getCompanyDashboardData());

  const companyLoginUserData = useRecoilValue(companyLoginUserDataAtom);
  const { companyId, isMultipleCompanyUser } = useRecoilValue(companyLoginUserDataSelector);

  const {
    iqfuMenuToggleState: { openPro, openIqfu, openChat, openJusetsu },
    iqfuMenuToggleHandler,
  } = useMenuToggle();

  const serviceList = companyLoginUserData?.services;
  const companyType = companyLoginUserData?.company_type;

  const hasIqfuService = serviceList?.includes('iqfu');
  const hasProService = serviceList?.includes('pro');
  const hasIqloudService = serviceList?.includes('cloud');
  const hasSellerChat = serviceList?.includes('seller_chat');
  const hasGeneralCompanyPurchaseCompanyChat = serviceList?.includes('general_company_purchase_company_chat');
  const hasFinancialAgencyConsulting = serviceList?.includes('financial_consulting');
  const hasPurchaseAgencySearch = serviceList?.includes('purchase_agency_search');

  const hasUnreadChat =
    dashboardData &&
    (dashboardData.unread_selling_room_count > 0 ||
      dashboardData.unread_seller_room_count > 0 ||
      dashboardData.unread_inquiry_count > 0 ||
      dashboardData.unread_consulting_room_count > 0);

  return (
    <div className={`${styles.container} ${spMenuOpen && styles.sp_open}`}>
      <div className={`${styles.container_inner}`}>
        <div className={styles.sp_header}>
          <div className={styles.logo_wrap}>
            {!spMenuOpen ? (
              <Link href={`/console/company/dashboard`}>
                <a>
                  <img src="/images/iqfu/dashboard/logo.svg" alt="イクラ不動産" />
                </a>
              </Link>
            ) : (
              <div />
            )}
            {spMenuOpen && <MdClose onClick={toggleSpMenu} />}
            {!spMenuOpen && <IoMenuSharp onClick={toggleSpMenu} />}
          </div>
        </div>
        <div className={styles.menu_container}>
          {hasProService && (
            <>
              <div
                className={`${styles.sideMenuTitle} ${styles.sideMenuTitle_pro}`}
                onClick={() => iqfuMenuToggleHandler(TargetMenu.PRO)}
              >
                イクラ不動産Pro
                <img
                  src="/images/iqloud/side-menu-open.svg"
                  alt="開閉アイコン"
                  className={`${styles.menuToggleIcon} ${openPro && styles.menuToggleIcon_isOpen}`}
                />
              </div>
              <Collapse in={openPro}>
                <div className={`${styles.sideMenuItemsWrapper} ${styles.sideMenuItemsWrapper_iqfu}`}>
                  <div className={styles.sideMenuItem}>
                    <Link href={`/console/company/iqfu_pro`}>
                      <a>
                        <div
                          className={`${styles.menuText} ${
                            router.pathname === `/console/company/iqfu_pro` ? styles.current : ''
                          }`}
                        >
                          成約事例検索
                        </div>
                      </a>
                    </Link>
                  </div>
                  <div className={styles.sideMenuItem}>
                    <Link href={`/console/company/iqfu_pro/map`}>
                      <a>
                        <div
                          className={`${styles.menuText} ${
                            router.pathname === `/console/company/iqfu_pro/map` ? styles.current : ''
                          }`}
                        >
                          成約事例Map検索
                        </div>
                      </a>
                    </Link>
                  </div>
                </div>
              </Collapse>
            </>
          )}

          {hasIqfuService && (
            <>
              <div
                className={`${styles.sideMenuTitle} ${styles.sideMenuTitle_iqfu}`}
                onClick={() => iqfuMenuToggleHandler(TargetMenu.IQFU)}
              >
                イクラ不動産
                <img
                  src="/images/iqloud/side-menu-open.svg"
                  alt="開閉アイコン"
                  className={`${styles.menuToggleIcon} ${openIqfu && styles.menuToggleIcon_isOpen}`}
                />
              </div>
              <Collapse in={openIqfu}>
                <div className={`${styles.sideMenuItemsWrapper} ${styles.sideMenuItemsWrapper_iqfu}`}>
                  <div className={styles.sideMenuItem}>
                    <Link href={`/console/company/dashboard`}>
                      <a>
                        <div
                          className={`${styles.menuText} ${
                            router.pathname === `/console/company/dashboard` ? styles.current : ''
                          }`}
                        >
                          管理画面トップ
                        </div>
                      </a>
                    </Link>
                  </div>
                  <div className={styles.sideMenuItem}>
                    <Link href={`/console/company/simulator`}>
                      <a>
                        <div
                          className={`${styles.menuText} ${
                            router.pathname === `/console/company/simulator` ? styles.current : ''
                          }`}
                        >
                          相場シミュレーター
                        </div>
                      </a>
                    </Link>
                  </div>
                  {hasPurchaseAgencySearch && (
                    <div className={styles.sideMenuItem}>
                      <Link href={`/console/company/purchase_agency/search`}>
                        <a>
                          <div
                            className={`${styles.menuText} ${
                              router.pathname === `/console/company/purchase_agency/search` ? styles.current : ''
                            }`}
                          >
                            買取専門会社を探す<span className={styles.newIcon}>NEW</span>
                          </div>
                        </a>
                      </Link>
                    </div>
                  )}
                  <div className={styles.sideMenuItem}>
                    <Link href={`/console/company/sale_cases/create`}>
                      <a>
                        <div
                          className={`${styles.menuText} ${
                            router.pathname === `/console/company/sale_cases/create` ? styles.current : ''
                          }`}
                        >
                          売却実績登録
                        </div>
                      </a>
                    </Link>
                  </div>
                  <div className={styles.sideMenuItem}>
                    <Link href={`/console/company/sale_cases`}>
                      <a>
                        <div
                          className={`${styles.menuText} ${
                            router.pathname === `/console/company/sale_cases` ? styles.current : ''
                          }`}
                        >
                          売却実績一覧
                        </div>
                      </a>
                    </Link>
                  </div>
                  {isMultipleCompanyUser ? (
                    <div className={styles.sideMenuItem}>
                      <Link href={`/console/company/companies_list`}>
                        <a>
                          <div
                            className={`${styles.menuText} ${
                              router.pathname === `/console/company/companies_list` ? styles.current : ''
                            }`}
                          >
                            会社一覧
                          </div>
                        </a>
                      </Link>
                    </div>
                  ) : (
                    <div className={styles.sideMenuItem}>
                      <Link href={`/console/company/company_detail/${companyId}`}>
                        <a>
                          <div
                            className={`${styles.menuText} ${
                              router.pathname === '/console/company/company_detail/[company_id]' ? styles.current : ''
                            }`}
                          >
                            会社情報
                          </div>
                        </a>
                      </Link>
                    </div>
                  )}
                  {isMultipleCompanyUser ? (
                    <div className={styles.sideMenuItem}>
                      <Link href={`/console/company/notification_emails_list`}>
                        <a>
                          <div
                            className={`${styles.menuText} ${
                              router.pathname === `/console/company/notification_emails_list` ? styles.current : ''
                            }`}
                          >
                            通知先一覧
                          </div>
                        </a>
                      </Link>
                    </div>
                  ) : (
                    <div className={styles.sideMenuItem}>
                      <Link href={`/console/company/notification_emails/${companyId}`}>
                        <a>
                          <div
                            className={`${styles.menuText} ${
                              router.pathname === `/console/company/notification_emails/[company_id]`
                                ? styles.current
                                : ''
                            }`}
                          >
                            通知先一覧
                          </div>
                        </a>
                      </Link>
                    </div>
                  )}
                  <div className={styles.sideMenuItem}>
                    <Link href={`/console/company/announcements`}>
                      <a>
                        <div
                          className={`${styles.menuText} ${
                            router.pathname === `/console/company/announcements` ? styles.current : ''
                          }`}
                        >
                          お知らせ
                        </div>
                      </a>
                    </Link>
                  </div>
                </div>
              </Collapse>
            </>
          )}

          {hasIqfuService && (
            <>
              <div
                className={`${styles.sideMenuTitle} ${styles.sideMenuTitle_chat}`}
                onClick={() => iqfuMenuToggleHandler(TargetMenu.CHAT)}
              >
                <div>
                  売却相談チャット
                  {hasUnreadChat && (
                    <img src="/images/iqfu/dashboard/unread_icon.svg" alt="未読" className={styles.menu_unread_icon} />
                  )}
                </div>
                <img
                  src="/images/iqloud/side-menu-open.svg"
                  alt="開閉アイコン"
                  className={`${styles.menuToggleIcon} ${openChat && styles.menuToggleIcon_isOpen}`}
                />
              </div>
              <Collapse in={openChat}>
                <div className={`${styles.sideMenuItemsWrapper} ${styles.sideMenuItemsWrapper_chat}`}>
                  <div className={styles.sideMenuItem}>
                    <Link href={`/console/company/chat/talk`}>
                      <a>
                        <div
                          className={`${styles.menuText} ${
                            router.pathname === `/console/company/chat/talk` ? styles.current : ''
                          }`}
                        >
                          売却相談
                          {dashboardData && dashboardData?.unread_selling_room_count > 0 && (
                            <img
                              src="/images/iqfu/dashboard/unread_icon.svg"
                              alt="未読"
                              className={styles.menu_unread_icon}
                            />
                          )}
                        </div>
                      </a>
                    </Link>
                  </div>
                  {hasSellerChat && (
                    <div className={styles.sideMenuItem}>
                      <Link href={`/console/company/chat/seller_contact`}>
                        <a>
                          <div
                            className={`${styles.menuText} ${
                              router.pathname === `/console/company/chat/seller_contact` ? styles.current : ''
                            }`}
                          >
                            リースバック紹介売主チャット
                            {dashboardData && dashboardData?.unread_seller_room_count > 0 && (
                              <img
                                src="/images/iqfu/dashboard/unread_icon.svg"
                                alt="未読"
                                className={styles.menu_unread_icon}
                              />
                            )}
                          </div>
                        </a>
                      </Link>
                    </div>
                  )}
                  <div className={styles.sideMenuItem}>
                    <Link href={`/console/company/chat/contact`}>
                      <a>
                        <div
                          className={`${styles.menuText} ${
                            router.pathname === `/console/company/chat/contact` ? styles.current : ''
                          }`}
                        >
                          運営問合せ
                          {dashboardData && dashboardData?.unread_inquiry_count > 0 && (
                            <img
                              src="/images/iqfu/dashboard/unread_icon.svg"
                              alt="未読"
                              className={styles.menu_unread_icon}
                            />
                          )}
                        </div>
                      </a>
                    </Link>
                  </div>
                  {hasFinancialAgencyConsulting && (
                    <div className={styles.sideMenuItem}>
                      <Link href={`/console/company/chat/consulting`}>
                        <a>
                          <div
                            className={`${styles.menuText} ${
                              router.pathname === `/console/company/chat/consulting` ? styles.current : ''
                            }`}
                          >
                            金融機関チャット
                            {dashboardData && dashboardData?.unread_consulting_room_count > 0 && (
                              <img
                                src="/images/iqfu/dashboard/unread_icon.svg"
                                alt="未読"
                                className={styles.menu_unread_icon}
                              />
                            )}
                          </div>
                        </a>
                      </Link>
                    </div>
                  )}
                  {hasGeneralCompanyPurchaseCompanyChat && (
                    <div className={styles.sideMenuItem}>
                      <Link href={`/console/company/chat/general_company_purchase_company_chat`}>
                        <a>
                          <div
                            className={`${styles.menuText} ${
                              router.pathname === `/console/company/chat/general_company_purchase_company_chat`
                                ? styles.current
                                : ''
                            }`}
                          >
                            {companyType === 'general_agency' ? '買取専門会社チャット' : '仲介会社チャット'}
                            {/* {dashboardData && dashboardData?.unread_consulting_room_count > 0 && (
                              <img
                                src="/images/iqfu/dashboard/unread_icon.svg"
                                alt="未読"
                                className={styles.menu_unread_icon}
                              />
                            )} */}
                          </div>
                        </a>
                      </Link>
                    </div>
                  )}
                  <div className={styles.sideMenuItem}>
                    <Link href={`/console/company/chat/mediation_agreement_reports/create`}>
                      <a>
                        <div
                          className={`${styles.menuText} ${
                            router.pathname === `/console/company/chat/mediation_agreement_reports/create`
                              ? styles.current
                              : ''
                          }`}
                        >
                          媒介契約報告
                        </div>
                      </a>
                    </Link>
                  </div>
                  <div className={styles.sideMenuItem}>
                    <Link href={`/console/company/chat/mediation_agreement_reports`}>
                      <a>
                        <div
                          className={`${styles.menuText} ${
                            router.pathname === `/console/company/chat/mediation_agreement_reports`
                              ? styles.current
                              : ''
                          }`}
                        >
                          媒介契約報告一覧
                        </div>
                      </a>
                    </Link>
                  </div>
                  <div className={styles.sideMenuItem}>
                    <Link href={`/console/company/chat/agreement_reports/create`}>
                      <a>
                        <div
                          className={`${styles.menuText} ${
                            router.pathname === `/console/company/chat/agreement_reports/create` ? styles.current : ''
                          }`}
                        >
                          売買契約報告
                        </div>
                      </a>
                    </Link>
                  </div>
                  <div className={styles.sideMenuItem}>
                    <Link href={`/console/company/chat/agreement_reports`}>
                      <a>
                        <div
                          className={`${styles.menuText} ${
                            router.pathname === `/console/company/chat/agreement_reports` ? styles.current : ''
                          }`}
                        >
                          売買契約報告一覧
                        </div>
                      </a>
                    </Link>
                  </div>
                </div>
              </Collapse>
            </>
          )}

          {hasIqloudService && (
            <>
              <div
                className={`${styles.sideMenuTitle} ${styles.sideMenuTitle_iqloud}`}
                onClick={() => iqfuMenuToggleHandler(TargetMenu.JUSETSU)}
              >
                重説イクラウド
                <img
                  src="/images/iqloud/side-menu-open.svg"
                  alt="開閉アイコン"
                  className={`${styles.menuToggleIcon} ${openJusetsu && styles.menuToggleIcon_isOpen}`}
                />
              </div>

              <Collapse in={openJusetsu}>
                <div className={`${styles.sideMenuItemsWrapper} ${styles.sideMenuItemsWrapper_iqloud}`}>
                  <div className={styles.sideMenuItem}>
                    <Link href={`/console/company/iqra_cloud`}>
                      <a>
                        <div
                          className={`${styles.menuText} ${
                            router.pathname === `/console/company/iqra_cloud` ? styles.current : ''
                          }`}
                        >
                          トップ
                        </div>
                      </a>
                    </Link>
                  </div>
                  <div className={styles.sideMenuItem}>
                    <Link href={`/console/company/iqra_cloud/jusetsu/list`}>
                      <a>
                        <div
                          className={`${styles.menuText} ${
                            router.pathname === `/console/company/iqra_cloud/jusetsu/list` ? styles.current : ''
                          }`}
                          data-test="companySideMenuLink-list"
                        >
                          重説一覧
                        </div>
                      </a>
                    </Link>
                  </div>
                  {!isMultipleCompanyUser && (
                    <div className={styles.sideMenuItem}>
                      <Link href={`/console/company/iqra_cloud/jusetsu/upload`}>
                        <a>
                          <div
                            className={`${styles.menuText} ${
                              router.pathname === `/console/company/iqra_cloud/jusetsu/upload` ? styles.current : ''
                            }`}
                            data-test="companySideMenuLink-upload"
                          >
                            重説アップロード
                          </div>
                        </a>
                      </Link>
                    </div>
                  )}
                </div>
              </Collapse>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
export default CompanySideMenu;
